/**
 * @author: Codex
 * @date: 2026/07/02
 * @desc: SelectMultiple 组件演示文件
 */
import React, { useState } from 'react';
import SelectMultiple from './index';

import './demo.scss';

const BASIC_OPTIONS = [
  { value: 'us', text: 'United States' },
  { value: 'jp', text: 'Japan' },
  { value: 'kr', text: 'South Korea' },
  { value: 'de', text: 'Germany' },
];

const SourceIcon = (props: { color: string }) => {
  const { color } = props;

  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <circle cx="8" cy="8" r="6" fill={color} />
    </svg>
  );
};

const BasicDemo: React.FC = () => {
  const [value, setValue] = useState<string[]>(['us', 'jp']);

  return (
    <div className="dbi-component-select-multiple-demo__inline">
      <SelectMultiple label="Country" value={value} onChange={setValue} options={BASIC_OPTIONS} />
      <span className="dbi-component-select-multiple-demo__value">当前值: {value.join(', ') || '(空)'}</span>
    </div>
  );
};

const SearchableDemo: React.FC = () => {
  const [value, setValue] = useState<string[]>([]);

  return (
    <div className="dbi-component-select-multiple-demo__inline">
      <SelectMultiple
        label="搜索"
        value={value}
        onChange={setValue}
        searchable
        searchPlaceholder="输入关键字搜索"
        options={BASIC_OPTIONS}
      />
      <span className="dbi-component-select-multiple-demo__value">当前值: {value.join(', ') || '(空)'}</span>
    </div>
  );
};

const AllOptionDemo: React.FC = () => {
  const [value, setValue] = useState<string[]>(['us', 'jp', 'kr', 'de']);

  return (
    <div className="dbi-component-select-multiple-demo__inline">
      <SelectMultiple label="含全选" value={value} onChange={setValue} showAllOption options={BASIC_OPTIONS} />
      <span className="dbi-component-select-multiple-demo__value">当前值: {value.join(', ') || '(空)'}</span>
    </div>
  );
};

const IconTooltipDemo: React.FC = () => {
  const [value, setValue] = useState<string[]>(['sensorTower']);

  return (
    <SelectMultiple
      label="数据源"
      value={value}
      onChange={setValue}
      tooltipMaxWidth={240}
      options={[
        {
          value: 'sensorTower',
          text: 'Sensor Tower',
          icon: <SourceIcon color="#4a90e2" />,
          tooltip: 'Sensor Tower 数据源，提供手游市场下载和收入数据',
        },
        {
          value: 'dataAi',
          text: 'data.ai',
          icon: <SourceIcon color="#e24a4a" />,
          tooltip: 'data.ai 数据源，提供应用市场分析和情报',
        },
        {
          value: 'steam',
          text: 'Steam',
          icon: <SourceIcon color="#34a853" />,
          tooltip: 'Steam 平台数据源',
        },
      ]}
    />
  );
};

const SizeDemo: React.FC = () => {
  const [smallValue, setSmallValue] = useState<string[]>(['us']);
  const [mediumValue, setMediumValue] = useState<string[]>(['us']);
  const [largeValue, setLargeValue] = useState<string[]>(['us']);

  return (
    <div className="dbi-component-select-multiple-demo__row">
      <SelectMultiple label="小尺寸" size="s" value={smallValue} onChange={setSmallValue} options={BASIC_OPTIONS} />
      <SelectMultiple label="中尺寸" size="m" value={mediumValue} onChange={setMediumValue} options={BASIC_OPTIONS} />
      <SelectMultiple label="大尺寸" size="l" value={largeValue} onChange={setLargeValue} options={BASIC_OPTIONS} />
    </div>
  );
};

const CustomButtonDemo: React.FC = () => {
  const [value, setValue] = useState<string[]>(['us']);

  return (
    <SelectMultiple
      value={value}
      onChange={setValue}
      options={BASIC_OPTIONS}
      button={(selectedOptions) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>自定义</span>
          <span>{selectedOptions.map((option) => option.text).join(', ') || '请选择'}</span>
        </div>
      )}
    />
  );
};

const EmptyDemo: React.FC = () => {
  const [value, setValue] = useState<string[]>([]);

  return (
    <SelectMultiple label="空态" value={value} onChange={setValue} searchable options={[]} />
  );
};

export const demoConfig = [
  {
    title: '基础用法',
    description: '普通下拉多选筛选器，默认直接触发 onChange',
    code: `<SelectMultiple
  label="Country"
  value={value}
  onChange={setValue}
  options={options}
/>`,
    element: <BasicDemo />,
  },
  {
    title: '搜索',
    description: '开启 searchable 后支持选项搜索',
    code: `<SelectMultiple
  label="搜索"
  value={value}
  onChange={setValue}
  searchable
  searchPlaceholder="输入关键字搜索"
  options={options}
/>`,
    element: <SearchableDemo />,
  },
  {
    title: '内置全选项',
    description: '设置 showAllOption 后展示 SelectMultiple 的全选项',
    code: `<SelectMultiple
  label="含全选"
  value={value}
  onChange={setValue}
  showAllOption
  options={options}
/>`,
    element: <AllOptionDemo />,
  },
  {
    title: '图标和气泡提示',
    description: '选项支持 icon 和 tooltip',
    code: `<SelectMultiple
  label="数据源"
  value={value}
  onChange={setValue}
  options={[
    {
      value: 'sensorTower',
      text: 'Sensor Tower',
      icon: <SourceIcon color="#4a90e2" />,
      tooltip: 'Sensor Tower 数据源',
    },
  ]}
/>`,
    element: <IconTooltipDemo />,
  },
  {
    title: '不同尺寸',
    description: '支持 Tea SelectMultiple 的 size 配置',
    code: `<SelectMultiple label="小尺寸" size="s" {...props} />
<SelectMultiple label="中尺寸" size="m" {...props} />
<SelectMultiple label="大尺寸" size="l" {...props} />`,
    element: <SizeDemo />,
  },
  {
    title: '自定义按钮',
    description: '通过 button 自定义下拉按钮展示',
    code: `<SelectMultiple
  value={value}
  onChange={setValue}
  options={options}
  button={(selectedOptions) => (
    <div>{selectedOptions.map((option) => option.text).join(', ')}</div>
  )}
/>`,
    element: <CustomButtonDemo />,
  },
  {
    title: '空态',
    description: '无选项或搜索无结果时展示空态',
    code: `<SelectMultiple
  label="空态"
  value={value}
  onChange={setValue}
  searchable
  options={[]}
/>`,
    element: <EmptyDemo />,
  },
];

export default demoConfig;
